import { useAppDispatch, useAppSelector } from "hooks/redux";
import { SectionViewResizeHandle } from "./SectionViewResizeHandle";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  websiteBuilderResizeSection,
  websiteBuilderUpdateSelectedNode,
} from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { clamp, max } from "lodash";
import { WEBSITE_BUILDER_MINIMUM_SECTION_HEIGHT } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { SectionViewResizeIndicator } from "./SectionViewResizeIndicator";
import { useWebsiteBuilderEditorContext } from "pages/secure/facility/websiteBuilder/editor/websiteBuilderEditorContext";
import { TWebsiteBuilderSectionEntity, websiteBuilderGetSectionEntityActiveLayoutData } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSectionEntity";
import { TWebsiteBuilderDevice } from "pages/secure/facility/websiteBuilder/editor/website/shared/device";

export interface IUseSectionViewResizeArgs {
  id: string;
  hovered: boolean;
  resizing: boolean;
  height: number;
  device: TWebsiteBuilderDevice;
  children: TWebsiteBuilderSectionEntity[];
}

interface ISectionViewResizeState {
  grabbing: boolean;
  height: number;
}

interface ISectionViewResizeRefState {
  currentTarget: Element;
  lowerClampHeight: number;
  prevHeight: number;
  nextHeight: number;
  resizing: boolean;
}

interface IUseSectionViewResize {
  viewResizeHeight: number;
  viewResizeHandle: JSX.Element;
  viewResizeIndicator: JSX.Element;
}

export function useSectionViewResize(args: IUseSectionViewResizeArgs): IUseSectionViewResize {
  const dispatch = useAppDispatch();

  const { activePageDomNodeRefMap } = useWebsiteBuilderEditorContext();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);

  const [resizeState, setResizeState] = useState<ISectionViewResizeState>({
    grabbing: false,
    height: null,
  });

  const resizeRefState = useRef<ISectionViewResizeRefState>({
    currentTarget: null,
    lowerClampHeight: null,
    prevHeight: null,
    nextHeight: null,
    resizing: false,
  });

  const viewResizeHeight: number = useMemo(() => {
    if (args.resizing && resizeState.height != null) {
      return resizeState.height;
    } else {
      return args.height;
    }
  }, [args.resizing, resizeState.height, args.height]);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const { lowerClampHeight, resizing } = resizeRefState.current;

      if (lowerClampHeight == null) {
        return;
      }

      const sectionRef = activePageDomNodeRefMap.current.get(args.id);
      const sectionBoundingRect = sectionRef?.getBoundingClientRect();

      if (!sectionBoundingRect) {
        return;
      }

      const yDelta = e.clientY - sectionBoundingRect.bottom;
      const updatedHeight = sectionBoundingRect.height + yDelta;

      const clampedUpdatedHeight = clamp(updatedHeight, lowerClampHeight, Infinity);

      resizeRefState.current.nextHeight = clampedUpdatedHeight;

      setResizeState(prev => ({
        ...prev,
        height: clampedUpdatedHeight,
      }));

      if (!resizing) {
        resizeRefState.current.resizing = true;

        dispatch(
          websiteBuilderUpdateSelectedNode({
            selectedNodeUpdates: {
              type: "section",
              editingMode: {
                type: "section-resize",
              },
            },
          }),
        );
      }
    },
    [args.id],
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      const { resizing, prevHeight, nextHeight, currentTarget } = resizeRefState.current;

      if (resizing) {
        if (prevHeight != null && nextHeight != null && Math.abs(prevHeight - nextHeight) > 1) {
          dispatch(
            websiteBuilderResizeSection(activePageId, deviceView, args.id, {
              id: args.id,
              device: deviceView,
              prevHeight,
              nextHeight,
            }),
          );
        } else {
          dispatch(
            websiteBuilderUpdateSelectedNode({
              selectedNodeUpdates: {
                type: "section",
                editingMode: {
                  type: "none",
                },
              },
            }),
          );
        }
      }

      currentTarget?.releasePointerCapture(e.pointerId);

      resizeRefState.current = {
        currentTarget: null,
        lowerClampHeight: null,
        prevHeight: null,
        nextHeight: null,
        resizing: false,
      };

      setResizeState({
        grabbing: false,
        height: null,
      });
    },
    [activePageId, args.id, deviceView],
  );

  useEffect(() => {
    if (resizeState.grabbing) {
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    }

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [resizeState.grabbing, handlePointerMove, handlePointerUp]);

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) {
      return;
    }

    const childrenBottoms = args.children.map(child => {
      const { position, dimensions } = websiteBuilderGetSectionEntityActiveLayoutData(child, args.device);

      return position.y + dimensions.height;
    });

    const maxChildBottom = max(childrenBottoms) ?? 0;
    const lowerClampHeight = Math.max(WEBSITE_BUILDER_MINIMUM_SECTION_HEIGHT, maxChildBottom);

    e.currentTarget.setPointerCapture(e.pointerId);

    resizeRefState.current.currentTarget = e.currentTarget;

    resizeRefState.current.lowerClampHeight = lowerClampHeight;

    resizeRefState.current.prevHeight = args.height;
    resizeRefState.current.nextHeight = args.height;

    resizeRefState.current.resizing = false;

    setResizeState(prev => ({
      ...prev,
      grabbing: true,
    }));
  }

  return {
    viewResizeHeight,
    viewResizeHandle: (
      <SectionViewResizeHandle hovered={args.hovered} grabbing={resizeState.grabbing} onPointerDown={onPointerDown} />
    ),
    viewResizeIndicator: <SectionViewResizeIndicator resizing={args.resizing} height={viewResizeHeight} />,
  };
}
